/* eslint-disable no-console */
import _ from "lodash";
import { db } from "@/firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  setDoc,
  query,
  orderBy,
  arrayUnion,
  arrayRemove,
  serverTimestamp
} from "firebase/firestore";
import { getNum } from "@/utils";

const state = {
  databucket: null,
  csvTargetData: [],
  newCsvTargetFileName: "",
  csvActualData: [],
  newCsvActualFileName: "",
  targetData: null,
  actualData: [],
  loadingDatabucket: false,
  changes: [],
  databucketRedeemableTransactions: [],
  loadingDatabucketRedeemableTransactions: false,
  revokeRedeemablePointsProcessing: false,
  lastRuleDeleted: false
};

const actions = {
  async loadDatabucket({ commit, getters }, databucketId) {
    if (!databucketId) {
      console.error("Databucket ID is required");
      return;
    }

    commit("setLoadingDatabucket", true);
    commit("setDatabucket", {});

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const databucketRef = doc(
        collection(programRef, "databuckets"),
        databucketId
      );
      const actualDataRef = collection(databucketRef, "actualData");
      const targetDataRef = collection(databucketRef, "targetData");

      const apiCalls = [
        getDoc(databucketRef),
        getDocs(query(actualDataRef, orderBy("created"))),
        getDoc(doc(targetDataRef, "latest"))
      ];

      const [
        databucketSnapshot,
        actualDataSnapshot,
        targetDataSnapshot
      ] = await Promise.all(apiCalls);

      if (!databucketSnapshot.exists()) {
        throw new Error("Databucket not found");
      }

      const databucketData = databucketSnapshot.data();
      const databucket = {
        id: databucketSnapshot.id,
        ...databucketData,
        created: databucketData.created?.toDate() || new Date(),
        updated: databucketData.updated?.toDate() || new Date()
      };

      const actualData = [];
      actualDataSnapshot.forEach(docSnapshot => {
        const data = docSnapshot.data();
        actualData.push({
          id: docSnapshot.id,
          ...data,
          created: data.created?.toDate() || new Date()
        });
      });

      const targetDataData = targetDataSnapshot.data();
      const targetData = targetDataData
        ? {
            ...targetDataData,
            updated: targetDataData.updated?.toDate() || new Date()
          }
        : null;

      commit("setDatabucket", databucket);
      commit("setActualData", actualData);
      commit("setTargetData", targetData);
    } catch (error) {
      console.error("Error loading databucket:", error);
      throw error.message || "Error occurred when loading data bucket";
    } finally {
      commit("setLoadingDatabucket", false);
    }
  },

  async createTargetCode({ commit, getters, dispatch }, payload) {
    if (!payload) {
      throw new Error("Target code payload is required");
    }

    const programRef = doc(db(), "programs", getters.programId);
    const databucketRef = doc(
      collection(programRef, "databuckets"),
      getters.databucket.id
    );

    try {
      await updateDoc(databucketRef, {
        targetCodes: arrayUnion(payload)
      });

      const newTargetCodes = [
        ...(getters.databucket.targetCodes || []),
        payload
      ];
      commit("patchDatabucket", { targetCodes: newTargetCodes });
      dispatch("setSnackbar", "Category Key added.");
    } catch (error) {
      console.error("Error creating target code:", error);
      throw error.message || "Error occurred when adding a new target code";
    }
  },

  async deleteTargetCode({ commit, getters, dispatch }, payload) {
    if (!payload) {
      throw new Error("Target code payload is required");
    }

    const programRef = doc(db(), "programs", getters.programId);
    const databucketRef = doc(
      collection(programRef, "databuckets"),
      getters.databucket.id
    );

    try {
      await updateDoc(databucketRef, {
        targetCodes: arrayRemove(payload)
      });

      const newTargetCodes = (getters.databucket.targetCodes || []).filter(
        item => item !== payload
      );
      commit("patchDatabucket", { targetCodes: newTargetCodes });
      dispatch("setSnackbar", "Target code deleted.");
    } catch (error) {
      console.error("Error deleting target code:", error);
      throw error.message || "Error occurred when deleting target code";
    }
  },

  async reorderTargetOrders({ getters, commit }, payload) {
    if (!Array.isArray(payload)) {
      throw new Error("Target codes array is required");
    }

    // Note: Make changes locally to show user visual changes immediately
    commit("patchDatabucket", { targetCodes: payload });

    const programRef = doc(db(), "programs", getters.programId);
    const databucketRef = doc(
      collection(programRef, "databuckets"),
      getters.databucket.id
    );

    try {
      await updateDoc(databucketRef, { targetCodes: payload });
    } catch (error) {
      console.error("Error reordering target codes:", error);
      throw error.message || "Error occurred when reordering target codes";
    }
  },

  async updateTargetMapping({ getters, commit, dispatch }, payload) {
    if (!payload) {
      throw new Error("Target mapping payload is required");
    }

    const programRef = doc(db(), "programs", getters.programId);
    const databucketRef = doc(
      collection(programRef, "databuckets"),
      getters.databucket.id
    );

    try {
      await updateDoc(databucketRef, { targetMapping: payload });
      commit("patchDatabucket", { targetMapping: payload });
      dispatch("setSnackbar", "Target Map Updated");
    } catch (error) {
      console.error("Error updating target mapping:", error);
      throw error.message || "Error occurred when updating target mapping";
    }
  },

  async updateActualMapping({ getters, commit, dispatch }, payload) {
    if (!payload) {
      throw new Error("Actual mapping payload is required");
    }

    const programRef = doc(db(), "programs", getters.programId);
    const databucketRef = doc(
      collection(programRef, "databuckets"),
      getters.databucket.id
    );

    try {
      await updateDoc(databucketRef, { actualMapping: payload });
      commit("patchDatabucket", { actualMapping: payload });
      dispatch("setSnackbar", "Actual mapping updated");
    } catch (error) {
      console.error("Error updating actual mapping:", error);
      throw error.message || "Error occurred when updating actual mapping";
    }
  },

  async updateTargetData({ getters, commit, dispatch }, payload) {
    if (!payload) {
      throw new Error("Target data payload is required");
    }

    const programRef = doc(db(), "programs", getters.programId);
    const databucketRef = doc(
      collection(programRef, "databuckets"),
      getters.databucket.id
    );
    const targetDataRef = collection(databucketRef, "targetData");

    try {
      const date = new Date();
      await setDoc(doc(targetDataRef, "latest"), {
        ...payload,
        updated: date
      });

      commit("setTargetData", { ...payload, updated: date });
      dispatch("setSnackbar", "Target data imported successfully");
    } catch (error) {
      console.error("Error updating target data:", error);
      throw error.message || "Error occurred when importing data";
    }
  },

  async createActualData({ getters, commit, dispatch }, payload) {
    if (!payload) {
      throw new Error("Actual data payload is required");
    }

    const programRef = doc(db(), "programs", getters.programId);
    const databucketRef = doc(
      collection(programRef, "databuckets"),
      getters.databucket.id
    );
    const actualDataRef = collection(databucketRef, "actualData");

    try {
      await addDoc(actualDataRef, {
        ...payload,
        created: serverTimestamp()
      });

      commit("createActualData", { ...payload, created: new Date() });
      dispatch("setSnackbar", "Data imported successfully");
    } catch (error) {
      console.error("Error creating actual data:", error);
      throw error.message || "Error occurred when importing data";
    }
  },

  async deleteActualData({ getters, commit, dispatch }, id) {
    if (!id) {
      throw new Error("Actual data ID is required");
    }

    const programRef = doc(db(), "programs", getters.programId);
    const databucketRef = doc(
      collection(programRef, "databuckets"),
      getters.databucket.id
    );
    const actualDataRef = collection(databucketRef, "actualData");

    try {
      await deleteDoc(doc(actualDataRef, id));
      commit("deleteActualData", id);
      dispatch("setSnackbar", "File was reversed");
    } catch (error) {
      console.error("Error deleting actual data:", error);
      throw error.message || "Error occurred when reversing file";
    }
  },

  async updateResult({ getters, dispatch, state }) {
    const {
      databucketPreviewColumns,
      databucketPointsFormula,
      targetCodes,
      runningTotalIndex,
      databucketColumnData,
      databucketTotalData
    } = getters;

    const { databucket } = state;

    if (!databucket || !databucket.id) {
      throw new Error("Databucket is required");
    }

    const programRef = doc(db(), "programs", getters.programId);
    const databucketRef = doc(
      collection(programRef, "databuckets"),
      getters.databucket.id
    );
    const resultsRef = collection(databucketRef, "results");

    try {
      const visibleColumns = databucketPreviewColumns.filter(item => item.show);
      const csvHeaders = [
        "Account Id",
        "Target Code",
        ...visibleColumns.map(item => item.label)
      ];

      const columnResult = databucketColumnData.reduce(
        (result, participant) => {
          const { data } = participant;
          const newData = [];
          targetCodes.forEach(targetCode => {
            const row = [participant.accountKey, targetCode];
            visibleColumns.forEach(header => {
              row.push(data[`${targetCode}-${header.column}`]);
            });
            newData.push(row);
          });

          return [...result, ...newData];
        },
        [csvHeaders]
      );

      const totalResult = databucketTotalData.reduce(
        (result, participant) => {
          const { data } = participant;
          const row = [participant.accountKey];
          visibleColumns.forEach(header => {
            row.push(data[header.column]);
          });
          return [...result, row];
        },
        [["Account Id", ...visibleColumns.map(item => item.label)]]
      );

      const columnCsvData = JSON.stringify(columnResult);
      const totalCsvData = JSON.stringify(totalResult);

      await Promise.all([
        setDoc(doc(resultsRef, "column"), {
          csvData: columnCsvData,
          updated: new Date()
        }),
        setDoc(doc(resultsRef, "total"), {
          csvData: totalCsvData,
          updated: new Date()
        })
      ]);

      dispatch("setSnackbar", "Results updated");
    } catch (error) {
      console.error("Error updating results:", error);
      throw error.message || "Error occurred when updating results";
    }
  },

  async deleteDatabucket({ getters, dispatch }, databucketId) {
    if (!databucketId) {
      throw new Error("Databucket ID is required");
    }

    const programRef = doc(db(), "programs", getters.programId);
    const databucketRef = doc(
      collection(programRef, "databuckets"),
      databucketId
    );

    try {
      await deleteDoc(databucketRef);
      dispatch("setSnackbar", "Data Bucket Deleted");
    } catch (error) {
      console.error("Error deleting databucket:", error);
      throw error.message || "Error occurred when deleting data bucket";
    }
  },

  // Enhanced action for getting databucket by ID
  async getDatabucketById({ getters }, databucketId) {
    if (!databucketId) {
      console.error("Databucket ID is required");
      return null;
    }

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const databucketRef = doc(
        collection(programRef, "databuckets"),
        databucketId
      );
      const databucketDoc = await getDoc(databucketRef);

      if (databucketDoc.exists()) {
        const data = databucketDoc.data();
        return {
          id: databucketDoc.id,
          ...data,
          created: data.created?.toDate() || new Date(),
          updated: data.updated?.toDate() || new Date()
        };
      } else {
        console.warn(`Databucket with ID ${databucketId} not found`);
        return null;
      }
    } catch (error) {
      console.error("Error getting databucket by ID:", error);
      return null;
    }
  },

  loadingDatabucket({ commit }, payload) {
    commit("setLoadingDatabucket", payload);
  },
  setLastRuleDeleted({ commit }, payload) {
    commit("setLastRuleDeleted", payload);
  },
  updateChanges({ commit }, payload) {
    commit("setChanges", payload);
  },
  patchDatabucket({ commit }, payload) {
    commit("patchDatabucket", payload);
  },
  setCsvTargetData({ commit }, payload) {
    commit("setCsvTargetData", payload);
  },
  setNewCsvTargetFileName({ commit }, payload) {
    commit("setNewCsvTargetFileName", payload);
  },
  setCsvActualData({ commit }, payload) {
    commit("setCsvActualData", payload);
  },
  setNewCsvActualFileName({ commit }, payload) {
    commit("setNewCsvActualFileName", payload);
  },
  setDatabucketRedeemableTransactions({ commit }, payload) {
    commit("setDatabucketRedeemableTransactions", payload);
  },
  setLoadingDatabucketRedeemableTransactions({ commit }, payload) {
    commit("setLoadingDatabucketRedeemableTransactions", payload);
  },
  setRevokeRedeemablePointsProcessing({ commit }, payload) {
    commit("setRevokeRedeemablePointsProcessing", payload);
  }
};

const mutations = {
  setLastRuleDeleted(state, payload) {
    state.lastRuleDeleted = payload;
  },

  setDatabucket(state, payload) {
    state.databucket = payload;
  },

  setDatabucketRedeemableTransactions(state, payload) {
    state.databucketRedeemableTransactions = payload;
  },

  setLoadingDatabucketRedeemableTransactions(state, payload) {
    state.loadingDatabucketRedeemableTransactions = payload;
  },

  setRevokeRedeemablePointsProcessing(state, payload) {
    state.revokeRedeemablePointsProcessing = payload;
  },

  setLoadingDatabucket(state, payload) {
    state.loadingDatabucket = payload;
  },

  setActualData(state, payload) {
    state.actualData = payload;
  },

  setTargetData(state, payload) {
    state.targetData = payload;
  },

  setCsvTargetData(state, payload) {
    state.csvTargetData = payload;
  },

  setNewCsvTargetFileName(state, payload) {
    state.newCsvTargetFileName = payload;
  },

  setCsvActualData(state, payload) {
    state.csvActualData = payload;
  },

  setNewCsvActualFileName(state, payload) {
    state.newCsvActualFileName = payload;
  },

  createActualData(state, payload) {
    state.actualData = [...state.actualData, payload];
  },

  deleteActualData(state, id) {
    state.actualData = state.actualData.filter(item => item.id !== id);
  },

  patchDatabucket(state, payload) {
    state.databucket = {
      ...state.databucket,
      ...payload
    };
  },

  setChanges(state, payload) {
    state.changes = payload;
  }
};

const getters = {
  lastRuleDeleted(state) {
    return state.lastRuleDeleted;
  },

  databucket(state) {
    return state.databucket;
  },

  changes(state) {
    return state.changes;
  },

  loadingDatabucketRedeemableTransactions(state) {
    return state.loadingDatabucketRedeemableTransactions;
  },

  databucketRedeemableTransactions(state) {
    return state.databucketRedeemableTransactions;
  },

  revokeRedeemablePointsProcessing(state) {
    return state.revokeRedeemablePointsProcessing;
  },

  loadingDatabucket(state) {
    return state.loadingDatabucket;
  },

  csvTargetData(state) {
    return state.csvTargetData;
  },

  newCsvTargetFileName(state) {
    return state.newCsvTargetFileName;
  },

  csvActualData(state) {
    return state.csvActualData;
  },

  newCsvActualFileName(state) {
    return state.newCsvActualFileName;
  },

  databucketsOffers(state, getters) {
    return getters.databucket?.offers || [];
  },

  databucketsQuizzes(state, getters) {
    return getters.databucket?.quizzes || [];
  },

  databucketEntityMap(state, getters) {
    return getters.databucket?.participantType === "member"
      ? getters.memberAccountKeysMap || {}
      : getters.companyAccountKeysMap || {};
  },

  targetCodes(state, getters) {
    return getters.databucket?.targetCodes || [];
  },

  targetConsumedTargetCodes(state) {
    if (!state.targetData) {
      return [];
    }
    return state.targetData.consumedTargetCodes || [];
  },

  actualConsumedTargetCodes(state) {
    const consumedTargetCodes = state.actualData.reduce((result, eachData) => {
      return [...result, ...(eachData.consumedTargetCodes || [])];
    }, []);

    return consumedTargetCodes;
  },

  allConsumedTargetCodes(state, getters) {
    return getters.targetConsumedTargetCodes.concat(
      getters.actualConsumedTargetCodes
    );
  },

  runningTotalIndex(state, getters) {
    const { actualConsumedTargetCodes } = getters;

    if (!actualConsumedTargetCodes.length) {
      return -1;
    }

    let runningTotalIndex = -1;
    for (let i = 0; i < getters.targetCodes.length; i += 1) {
      const tc = getters.targetCodes[i];
      const found = actualConsumedTargetCodes.find(item => item == tc);
      if (found) {
        runningTotalIndex = i;
      }
    }

    return runningTotalIndex;
  },

  mergedActualData(state) {
    return state.actualData.reduce((aggr, eachData) => {
      const { consumedTargetCodes = [] } = eachData;
      return (eachData.data || []).reduce((subAggr, row) => {
        const idx = subAggr.findIndex(
          item => item["Account Key"] === row["Account Key"]
        );
        if (idx === -1) {
          return [...subAggr, row];
        }

        const mergedRow = {
          ...subAggr[idx]
        };

        consumedTargetCodes.forEach(targetCode => {
          const newVal = row[targetCode] || "0";
          const oldVal = mergedRow[targetCode] || "0";

          mergedRow[targetCode] = getNum(newVal) + getNum(oldVal);
        });

        return [...subAggr.slice(0, idx), mergedRow, ...subAggr.slice(idx + 1)];
      }, aggr);
    }, []);
  },

  mergedTargetData(state) {
    return (state.targetData || {}).data || [];
  },

  databucketParticipants(state, getters) {
    const allAccountKeyValues = getters.mergedTargetData.map(
      item => item["Account Key"]
    );
    const uniqAccountKeyValues = _.uniq(allAccountKeyValues);

    return uniqAccountKeyValues.reduce((result, accountKey) => {
      const entityData = getters.databucketEntityMap[accountKey];
      if (!entityData) {
        return result;
      }

      const label = entityData.title || entityData.name || accountKey;

      return [
        ...result,
        {
          label,
          value: accountKey,
          ...entityData
        }
      ];
    }, []);
  },

  databucketPointsFormula(state) {
    return state.databucket?.pointsFormula || [];
  },

  databucketPreviewColumns(state) {
    if (state.databucket?.previewColumns) {
      const offerExist =
        state.databucket.previewColumns.findIndex(
          el => el.column == "offerPoints"
        ) !== -1;
      if (offerExist) {
        return state.databucket.previewColumns;
      }
      return state.databucket.previewColumns.concat([
        {
          column: "offerPoints",
          label: "Claim Points",
          calculated: true,
          show: true
        },
        {
          column: "quizPoints",
          label: "Quiz Points",
          calculated: true,
          show: true
        },
        {
          column: "totalPoints",
          label: "Total Points",
          calculated: true,
          show: true
        }
      ]);
    }
    return [
      { column: "actual", label: "Actual", show: true },
      { column: "target", label: "Target", show: true },
      { column: "diff", label: "Difference", calculated: true, show: true },
      { column: "percent", label: "Percentage", calculated: true, show: true },
      {
        column: "points",
        label: "Target Points",
        calculated: true,
        show: true
      },
      {
        column: "offerPoints",
        label: "Claim Points",
        calculated: true,
        show: true
      },
      {
        column: "quizPoints",
        label: "Quiz Points",
        calculated: true,
        show: true
      },
      {
        column: "totalPoints",
        label: "Total Points",
        calculated: true,
        show: true
      }
    ];
  },

  // Enhanced getters for better data access
  getCurrentDatabucketById: state => id => {
    return state.databucket?.id === id ? state.databucket : null;
  },

  actualDataCount(state) {
    return state.actualData.length;
  },

  hasTargetData(state) {
    return state.targetData !== null && state.targetData !== undefined;
  },

  hasDatabucket(state) {
    return state.databucket !== null && state.databucket !== undefined;
  },

  databucketColumnData(state, getters) {
    const {
      targetCodes,
      runningTotalIndex,
      databucketPointsFormula,
      databucketParticipants,
      mergedTargetData,
      mergedActualData,
      databucketPreviewColumns,
      databucketsOffers,
      databucketsQuizzes,
      databucket
    } = getters;

    if (!databucket) {
      return [];
    }

    const columnData = databucketParticipants.map(participant => {
      const selectedTargetData =
        mergedTargetData.find(
          item => item["Account Key"] === participant.value
        ) || {};
      const selectedActualData =
        mergedActualData.find(
          item => item["Account Key"] === participant.value
        ) || {};

      const data = {};

      targetCodes.forEach((targetCode, targetCodeIdx) => {
        databucketPreviewColumns.forEach(mapping => {
          const { column } = mapping;
          const cellKey = `${targetCode}-${column}`;
          let cellVal = "";

          const targetVal = getNum(selectedTargetData[targetCode]);
          const actualVal = getNum(selectedActualData[targetCode]);
          const diffVal = actualVal - targetVal;
          const percentVal = (actualVal / targetVal) * 100;
          let points = 0;

          if (column === "target") {
            cellVal = targetVal;
          } else if (column === "actual") {
            cellVal = actualVal;
          } else if (column === "diff") {
            if (targetCodeIdx <= runningTotalIndex) {
              cellVal = diffVal;
            }
          } else if (column === "percent") {
            if (targetCodeIdx <= runningTotalIndex) {
              if (percentVal === Infinity) {
                cellVal = 0;
              } else {
                cellVal = percentVal;
              }
            }
          } else if (column === "points") {
            if (targetCodeIdx <= runningTotalIndex) {
              for (let i = 0; i < databucketPointsFormula.length; i += 1) {
                // redeemable points based on target achievement
                const condition = databucketPointsFormula[i];

                // prepare conditional statement
                const fixedStatement = condition.statement
                  .replace(/__actual/gi, actualVal)
                  .replace(/__target/gi, targetVal)
                  .replace(/__percent/gi, percentVal)
                  .replace(/__diff/gi, diffVal);

                // evaluate statement to find if conditions are met, returns true or false
                const met = Function("return" + fixedStatement)();

                // prepare dynamic points value
                const dynamicStatement = condition.dynamicPoints
                  .replace(/__actual/gi, actualVal)
                  .replace(/__target/gi, targetVal)
                  .replace(/__percent/gi, percentVal)
                  .replace(/__diff/gi, diffVal);

                // evaluate statement to see what points are to be awarded by the dynamic calculation
                const dynamicPointsEval = condition.selectedVariable
                  ? Function("return" + dynamicStatement)()
                  : 0;

                const dynamicPoints = condition.selectedVariable
                  ? parseFloat(dynamicPointsEval)
                  : 0; // number

                const fixedPoints = condition.selectedFixed
                  ? parseFloat(condition.result)
                  : 0; // number

                if (met) {
                  if (targetVal === 0 || null) {
                    points = 0;
                  } else {
                    points = fixedPoints + dynamicPoints;
                  }
                  break;
                }
              }
              cellVal = Math.floor(points);
            }
          } else if (column === "offerPoints") {
            // if (targetCodeIdx <= runningTotalIndex) {
            let offerPoints = 0;
            if (databucket.participantType === "company") {
              const targetOffers = databucketsOffers.filter(
                el =>
                  el.targetCode === targetCode && el.company === participant.id
              );
              offerPoints = targetOffers.reduce((acc, offer) => {
                return acc + +offer.points;
              }, 0);
            } else {
              const targetOffers = databucketsOffers.filter(
                el =>
                  el.targetCode === targetCode && el.member === participant.id
              );
              offerPoints = targetOffers.reduce((acc, offer) => {
                return acc + +offer.points;
              }, 0);
            }

            cellVal = offerPoints == 0 ? 0 : Math.floor(offerPoints);
            // }
          } else if (column === "quizPoints") {
            // if (targetCodeIdx <= runningTotalIndex) {
            let quizPoints = 0;
            if (databucket.participantType === "company") {
              const targetQuizzes = databucketsQuizzes.filter(
                el =>
                  el.targetCode === targetCode && el.company === participant.id
              );
              quizPoints = targetQuizzes.reduce((acc, quiz) => {
                return acc + +quiz.points;
              }, 0);
            } else {
              const targetQuizzes = databucketsQuizzes.filter(
                el =>
                  el.targetCode === targetCode && el.member === participant.id
              );
              quizPoints = targetQuizzes.reduce((acc, quiz) => {
                return acc + +quiz.points;
              }, 0);
            }

            cellVal = quizPoints == 0 ? 0 : Math.floor(quizPoints);
            // }
          } else if (column === "totalPoints") {
            // if (targetCodeIdx <= runningTotalIndex) {
            for (let i = 0; i < databucketPointsFormula.length; i += 1) {
              const condition = databucketPointsFormula[i];

              // prepare conditional statement
              const fixedStatement = condition.statement
                .replace(/__actual/gi, actualVal)
                .replace(/__target/gi, targetVal)
                .replace(/__percent/gi, percentVal)
                .replace(/__diff/gi, diffVal);

              // evaluate statement to find if conditions are met, returns true or false
              const met = Function("return" + fixedStatement)();

              // prepare dynamic points value
              const dynamicStatement = condition.dynamicPoints
                .replace(/__actual/gi, actualVal)
                .replace(/__target/gi, targetVal)
                .replace(/__percent/gi, percentVal)
                .replace(/__diff/gi, diffVal);

              // evaluate statement to see what points are to be awarded by the dynamic calculation
              const dynamicPointsEval = condition.selectedVariable
                ? Function("return" + dynamicStatement)()
                : 0;

              const dynamicPoints = condition.selectedVariable
                ? parseFloat(dynamicPointsEval)
                : 0; // number

              const fixedPoints = condition.selectedFixed
                ? parseFloat(condition.result)
                : 0; // number

              if (met) {
                if (targetVal === 0 || null) {
                  points = 0;
                } else {
                  points = fixedPoints + dynamicPoints;
                }
                break;
              }
            }
            let offerPoints = 0;
            if (databucket.participantType === "company") {
              const targetOffers = databucketsOffers.filter(
                el =>
                  el.targetCode === targetCode && el.company === participant.id
              );
              offerPoints = targetOffers.reduce((acc, offer) => {
                return acc + +offer.points;
              }, 0);
            } else {
              const targetOffers = databucketsOffers.filter(
                el =>
                  el.targetCode === targetCode && el.member === participant.id
              );
              offerPoints = targetOffers.reduce((acc, offer) => {
                return acc + +offer.points;
              }, 0);
            }
            let quizPoints = 0;
            if (databucket.participantType === "company") {
              const targetQuizzes = databucketsQuizzes.filter(
                el =>
                  el.targetCode === targetCode && el.company === participant.id
              );
              quizPoints = targetQuizzes.reduce((acc, quiz) => {
                return acc + +quiz.points;
              }, 0);
            } else {
              const targetQuizzes = databucketsQuizzes.filter(
                el =>
                  el.targetCode === targetCode && el.member === participant.id
              );
              quizPoints = targetQuizzes.reduce((acc, quiz) => {
                return acc + +quiz.points;
              }, 0);
            }
            const value =
              Math.floor(points) +
              Math.floor(offerPoints) +
              Math.floor(quizPoints);
            cellVal = value == 0 ? 0 : value;
            // }
          }

          data[cellKey] = cellVal;
        });
      });

      return {
        accountKey: participant.value,
        data
      };
    });
    return columnData;
  },

  databucketTotalData(state, getters) {
    const {
      // runningTotalIndex,
      databucketColumnData,
      targetCodes,
      databucketPreviewColumns
    } = getters;

    if (!getters.databucket) {
      return [];
    }

    // const targetCodesByRunningTotal = targetCodes.slice(0, runningTotalIndex + 1);
    const targetCodesByRunningTotal = [...targetCodes];

    return databucketColumnData.map(participant => {
      const columnData = participant.data;
      const {
        targetTotal,
        actualTotal,
        diffTotal,
        pointsTotal,
        offerPointsTotal,
        quizPointsTotal,
        totalPointsTotal
      } = targetCodesByRunningTotal.reduce(
        (result, targetCode) => {
          const target = +columnData[`${targetCode}-target`];
          const actual = +columnData[`${targetCode}-actual`];
          const diff = +columnData[`${targetCode}-diff`];
          const points = +columnData[`${targetCode}-points`];
          const offerPoints = +columnData[`${targetCode}-offerPoints`];
          const quizPoints = +columnData[`${targetCode}-quizPoints`];
          const totalPoints = +columnData[`${targetCode}-totalPoints`];
          return {
            targetTotal: result.targetTotal + target,
            actualTotal: result.actualTotal + actual,
            diffTotal: result.diffTotal + diff,
            pointsTotal: result.pointsTotal + points,
            offerPointsTotal: result.offerPointsTotal + offerPoints,
            quizPointsTotal: result.quizPointsTotal + quizPoints,
            totalPointsTotal: result.totalPointsTotal + totalPoints
          };
        },
        {
          targetTotal: 0,
          actualTotal: 0,
          diffTotal: 0,
          pointsTotal: 0,
          offerPointsTotal: 0,
          quizPointsTotal: 0,
          totalPointsTotal: 0
        }
      );

      const totalData = {};
      databucketPreviewColumns.forEach(mapping => {
        let val;
        if (mapping.column === "target") {
          val = targetTotal;
        } else if (mapping.column === "actual") {
          val = actualTotal;
        } else if (mapping.column === "diff") {
          val = diffTotal;
        } else if (mapping.column === "points") {
          val = pointsTotal;
        } else if (mapping.column === "percent") {
          val = (actualTotal / targetTotal) * 100;
        } else if (mapping.column === "offerPoints") {
          val = offerPointsTotal;
        } else if (mapping.column === "quizPoints") {
          val = quizPointsTotal;
        } else if (mapping.column === "totalPoints") {
          val = totalPointsTotal;
        }
        totalData[mapping.column] = val;
      });

      return {
        accountKey: participant.accountKey,
        data: totalData
      };
    });
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};
